// ============================================================================
// TournamentStartWatcher - global "your tournament is starting" experience
// ============================================================================
// Mounted once at app level. Watches every tournament the user is registered
// in and provides:
//   1. PRE-START PILL: a countdown banner when a registered tournament's
//      scheduled start is inside 2 minutes ("starts in 1:45").
//   2. SEATING CARD: when the tournament starts and the user has a seat,
//      a prominent card with a 15s auto-join countdown. If the user is in
//      a LIVE HAND at another table, it NEVER yanks them - it flips to
//      "joining after this hand" automatically (with a JOIN NOW override),
//      and follows through the moment their hand ends.
// Dismissing stamps sessionStorage so the card shows once per tournament;
// MY TABLES remains the manual path afterwards.
// ============================================================================
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db, auth } from '../../firebase';

interface WatchedTournament {
  id: string;
  name: string;
  status: string;
  startTime?: any;
  playerTableMap?: Record<string, string>;
  tableNumberById?: Record<string, number>;
}

const ACTIVE_STATUSES = ['registering', 'scheduled', 'upcoming', 'late_reg', 'running', 'active', 'in_progress'];

const TournamentStartWatcher: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [uid, setUid] = useState<string | null>(auth.currentUser?.uid || null);
  const [tournaments, setTournaments] = useState<WatchedTournament[]>([]);
  const [nowTick, setNowTick] = useState(Date.now());
  const [seatCard, setSeatCard] = useState<{ tid: string; name: string; tableId: string } | null>(null);
  const [joinCountdown, setJoinCountdown] = useState(8);
  const [afterHand, setAfterHand] = useState(false);
  const [myHandLive, setMyHandLive] = useState(false);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => auth.onAuthStateChanged(u => setUid(u?.uid || null)), []);

  // Registered-tournament subscription (array-contains needs no index).
  useEffect(() => {
    if (!uid) { setTournaments([]); return; }
    const q = query(collection(db, 'tournaments'), where('registeredPlayers', 'array-contains', uid));
    const unsub = onSnapshot(q, snap => {
      setTournaments(snap.docs
        .map(d => ({ id: d.id, ...(d.data() as any) }))
        .filter((t: any) => ACTIVE_STATUSES.includes(t.status)));
    }, () => setTournaments([]));
    return () => unsub();
  }, [uid]);

  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const currentTableId = location.pathname.startsWith('/game/')
    ? location.pathname.split('/game/')[1]?.split('?')[0]
    : null;

  // Track whether the user is in a LIVE hand at the table they're viewing -
  // that's what decides yank vs. wait.
  useEffect(() => {
    if (!currentTableId || !uid) { setMyHandLive(false); return; }
    const unsub = onSnapshot(doc(db, 'tables', currentTableId), snap => {
      const gs: any = snap.exists() ? (snap.data() as any).gameState : null;
      const me = (gs?.players || []).find((p: any) => p?.id === uid);
      setMyHandLive(!!(gs && gs.status === 'playing' && gs.round
        && gs.round !== 'ended' && gs.round !== 'waiting'
        && gs.showdownComplete !== true
        && me && !me.folded && !me.eliminated));
    }, () => setMyHandLive(false));
    return () => unsub();
  }, [currentTableId, uid]);

  // PRE-START pill: the soonest registered tournament starting inside 2min.
  const preStart = useMemo(() => {
    let best: { name: string; msLeft: number } | null = null;
    for (const t of tournaments) {
      if (!['registering', 'scheduled', 'upcoming'].includes(t.status)) continue;
      const ms = t.startTime?.toMillis?.() ?? (typeof t.startTime === 'number' ? t.startTime : 0);
      if (!ms) continue;
      const left = ms - nowTick;
      if (left > 0 && left <= 120_000 && (!best || left < best.msLeft)) {
        best = { name: t.name || 'Tournament', msLeft: left };
      }
    }
    return best;
  }, [tournaments, nowTick]);

  // SEATING detection: a started tournament with a seat for me that I'm
  // not already viewing, shown once per tournament. If I'm viewing ANY
  // table of that tournament I'm clearly already in - table moves
  // (balancer, final-table merge) are handled by follow-the-seat, and
  // popping "JOIN NOW" at someone who is sitting in the event was pure
  // noise. Stamp it seen so it never shows for this tournament again.
  useEffect(() => {
    if (!uid || seatCard) return;
    for (const t of tournaments) {
      const seat = t.playerTableMap?.[uid];
      if (!seat) continue;
      const viewingThisTournament = !!currentTableId
        && (currentTableId === seat || currentTableId.startsWith(`${t.id}_`));
      if (viewingThisTournament) {
        try { sessionStorage.setItem(`pss:tstartSeen:${t.id}`, '1'); } catch { /* noop */ }
        continue;
      }
      try {
        if (sessionStorage.getItem(`pss:tstartSeen:${t.id}`)) continue;
        if (sessionStorage.getItem(`pss:leftTournament:${t.id}`)) continue;
        sessionStorage.setItem(`pss:tstartSeen:${t.id}`, '1');
      } catch { /* noop */ }
      setSeatCard({ tid: t.id, name: t.name || 'Tournament', tableId: seat });
      // 8s: the first hand holds a 20s on-table countdown, so auto-joined
      // players land with a good chunk of it still on the clock.
      setJoinCountdown(8);
      setAfterHand(false);
      return;
    }
  }, [tournaments, uid, currentTableId, seatCard]);

  // AUTO-DISMISS: the card's job is done the moment the user is at (any
  // table of) the tournament - whether they clicked JOIN NOW, navigated
  // themselves, or follow-the-seat moved them. It used to hang around
  // saying "you'll join the moment it ends" to someone already seated.
  useEffect(() => {
    if (!seatCard || !currentTableId) return;
    if (currentTableId === seatCard.tableId || currentTableId.startsWith(`${seatCard.tid}_`)) {
      setSeatCard(null);
      setAfterHand(false);
    }
  }, [currentTableId, seatCard]);

  const goToSeat = React.useCallback(() => {
    if (!seatCard) return;
    const dest = `/game/${seatCard.tableId}`;
    setSeatCard(null);
    setAfterHand(false);
    navigate(dest);
  }, [seatCard, navigate]);

  // Countdown driver: while a live hand is in progress we DON'T count
  // down - the card waits as "after this hand" instead of yanking.
  useEffect(() => {
    if (!seatCard || afterHand) { if (countdownRef.current) clearInterval(countdownRef.current); return; }
    if (myHandLive) { setAfterHand(true); return; }
    countdownRef.current = setInterval(() => {
      setJoinCountdown(prev => {
        if (prev <= 1) { goToSeat(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seatCard, afterHand, myHandLive]);

  // AFTER-HAND follow-through: the moment my hand ends, take the seat.
  useEffect(() => {
    if (!seatCard || !afterHand) return;
    if (!myHandLive) goToSeat();
  }, [afterHand, myHandLive, seatCard, goToSeat]);

  return (
    <>
      {/* PRE-START countdown pill */}
      {preStart && !seatCard && (
        <div style={{
          position: 'fixed', top: '10px', left: '50%', transform: 'translateX(-50%)',
          zIndex: 1350, background: 'rgba(10, 14, 22, 0.96)',
          border: '1px solid rgba(212, 175, 55, 0.6)', borderRadius: 20,
          padding: '8px 20px', pointerEvents: 'none', textAlign: 'center',
        }}>
          <span style={{ color: '#fde68a', fontSize: 13, fontWeight: 800, letterSpacing: '0.06em' }}>
            {preStart.name} starts in{' '}
            {Math.floor(preStart.msLeft / 60000)}:{String(Math.floor((preStart.msLeft % 60000) / 1000)).padStart(2, '0')}
          </span>
          <span style={{ color: '#94a3b8', fontSize: 11, marginLeft: 10 }}>
            Wrap up your current hands
          </span>
        </div>
      )}

      {/* SEATING card */}
      {seatCard && (
        <div style={{
          position: 'fixed', top: '14px', left: '50%', transform: 'translateX(-50%)',
          zIndex: 1350, width: 'min(94vw, 460px)',
          background: 'linear-gradient(160deg, rgba(13,31,46,0.98), rgba(6,14,24,0.98))',
          border: '1px solid rgba(212, 175, 55, 0.65)', borderRadius: 16,
          padding: '16px 20px', boxShadow: '0 18px 60px rgba(0,0,0,0.65)',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#d4af37', fontSize: 11, fontWeight: 800, letterSpacing: '0.24em' }}>
                TOURNAMENT STARTING
              </div>
              <div style={{ color: '#fff', fontSize: 16, fontWeight: 700, marginTop: 3 }}>
                {seatCard.name}
              </div>
              <div style={{ color: '#94a3b8', fontSize: 12.5, marginTop: 4 }}>
                {afterHand
                  ? 'You’re in a hand - your seat is held and you’ll join the moment it ends.'
                  : `Your seat is ready. Taking you there in ${joinCountdown}s…`}
              </div>
            </div>
            <button
              onClick={() => { setSeatCard(null); setAfterHand(false); }}
              title="Dismiss - join later from MY TABLES"
              style={{
                background: 'transparent', border: 'none', color: '#64748b',
                fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: 2,
              }}
            >×</button>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <button
              onClick={goToSeat}
              style={{
                flex: 1, background: 'linear-gradient(135deg, #f5d976, #d4af37 60%, #a97f1f)',
                border: 'none', color: '#181205', borderRadius: 16, padding: '10px 0',
                fontWeight: 800, fontSize: 13, letterSpacing: '0.1em', cursor: 'pointer',
              }}
            >
              JOIN NOW
            </button>
            {myHandLive && !afterHand && (
              <button
                onClick={() => setAfterHand(true)}
                style={{
                  flex: 1, background: 'transparent',
                  border: '1px solid rgba(212,175,55,0.55)', color: '#fde68a',
                  borderRadius: 16, padding: '10px 0', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                }}
              >
                AFTER THIS HAND
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default TournamentStartWatcher;
